import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  Upload, FileText, AlertCircle, CheckCircle, Download, Edit3,
  Trash2, Check, X, Eye, Scale, BookOpen, ChevronDown, ChevronUp, Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITY_CONFIG = {
  critical: { label: "Critique", bg: "bg-red-100 text-red-800 border-red-300", bar: "bg-red-500" },
  high:     { label: "Élevé",    bg: "bg-orange-100 text-orange-800 border-orange-300", bar: "bg-orange-500" },
  medium:   { label: "Moyen",   bg: "bg-amber-100 text-amber-800 border-amber-300", bar: "bg-amber-500" },
  low:      { label: "Faible",  bg: "bg-green-100 text-green-800 border-green-300", bar: "bg-green-500" },
  "Critique": { label: "Critique", bg: "bg-red-100 text-red-800 border-red-300", bar: "bg-red-500" },
  "Élevé":    { label: "Élevé",    bg: "bg-orange-100 text-orange-800 border-orange-300", bar: "bg-orange-500" },
  "Moyen":    { label: "Moyen",   bg: "bg-amber-100 text-amber-800 border-amber-300", bar: "bg-amber-500" },
  "Faible":   { label: "Faible",  bg: "bg-green-100 text-green-800 border-green-300", bar: "bg-green-500" },
};

export default function Documents({ user, cabinet, onLogout }) {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [applying, setApplying] = useState(false);
  const [editingClauseIndex, setEditingClauseIndex] = useState(null);
  const [editedSuggestion, setEditedSuggestion] = useState("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [expandedClauses, setExpandedClauses] = useState({});
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const LIMIT = 20;

  useEffect(() => { loadDocuments(0, false); }, []);

  const loadDocuments = async (currentSkip = 0, append = false) => {
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip: currentSkip, limit: LIMIT }
      });
      const { documents: newDocs, total: newTotal } = response.data;
      setTotal(newTotal);
      setDocuments(prev => append ? [...prev, ...newDocs] : newDocs);
      setSkip(currentSkip + newDocs.length);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expirée");
        setTimeout(() => window.location.href = '/auth', 2000);
      }
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    await loadDocuments(skip, true);
    setLoadingMore(false);
  };

  const loadFullDocument = async (docId) => {
    setLoadingDoc(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDocument(response.data);
      setSelectedSuggestions([]);
      setExpandedClauses({});
    } catch { toast.error("Erreur chargement document"); }
    finally { setLoadingDoc(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploadé !");
      await loadDocuments();
      await handleAnalyze(response.data.document_id);
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); }
  };

  const handleAnalyze = async (docId) => {
    setAnalyzing(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      await axios.post(`${API}/documents/${docId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadFullDocument(docId);
      toast.success("Analyse terminée !");
      await loadDocuments();
    } catch { toast.error("Erreur analyse"); }
    finally { setAnalyzing(false); }
  };

  const toggleSuggestion = (clauseIndex) => {
    setSelectedSuggestions(prev =>
      prev.includes(clauseIndex)
        ? prev.filter(i => i !== clauseIndex)
        : [...prev, clauseIndex]
    );
  };

  const toggleExpandClause = (idx) => {
    setExpandedClauses(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Build preview text: full document with modifications applied
  const buildPreviewContent = () => {
    if (!selectedDocument?.content) return null;
    let fullText = selectedDocument.content;
    const clauses = selectedDocument.analysis?.sensitive_clauses || [];
    
    // Apply selected modifications
    const applied = [];
    selectedSuggestions.forEach(idx => {
      const clause = clauses[idx];
      if (clause?.original_text && clause?.suggested_modification) {
        fullText = fullText.replace(clause.original_text, `[[MOD_START]]${clause.suggested_modification}[[MOD_END]]`);
        applied.push(clause.type);
      }
    });
    return { text: fullText, applied };
  };

  const handleApplyModifications = async () => {
    if (selectedSuggestions.length === 0) {
      toast.error("Sélectionnez au moins une suggestion");
      return;
    }
    setApplying(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const modifications = selectedSuggestions.map(index => {
        const clause = selectedDocument.analysis.sensitive_clauses[index];
        return { clause_index: index, new_text: clause.suggested_modification };
      });
      const response = await axios.post(
        `${API}/documents/${selectedDocument.id}/apply-modifications`,
        { modifications },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedDocument.filename.replace(/\.[^/.]+$/, '')}_modifie.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Document complet téléchargé avec modifications !");
      setSelectedSuggestions([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur application modifications");
    } finally { setApplying(false); }
  };

  const getRiskBg = (score) => {
    if (score >= 70) return "bg-green-50 border-green-200";
    if (score >= 30) return "bg-amber-50 border-amber-200";
    return "bg-red-50 border-red-200";
  };
  const getRiskColor = (score) => {
    if (score >= 70) return "text-green-700";
    if (score >= 30) return "text-amber-700";
    return "text-red-700";
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-slate-900">Analyse de Documents</h1>
            <p className="text-slate-600 mt-1">Analyse IA contextuelle avec suggestions juridiques précises</p>
          </div>
          <label>
            <input type="file" onChange={handleUpload} className="hidden" accept=".pdf,.doc,.docx,.txt" />
            <Button disabled={uploading} className="gap-2 bg-blue-600 hover:bg-blue-700">
              <Upload className="h-4 w-4" />
              {uploading ? "Upload en cours..." : "Uploader un document"}
            </Button>
          </label>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left - Document List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-700">Mes Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
                {documents.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-40 text-slate-400" />
                    <p className="text-sm text-slate-500">Aucun document</p>
                  </div>
                ) : (
                  documents.map(doc => {
                    const safetyScore = doc.analysis ? 100 - doc.analysis.risk_score : null;
                    return (
                      <button
                        key={doc.id}
                        onClick={() => loadFullDocument(doc.id)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition ${
                          selectedDocument?.id === doc.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <FileText className="h-4 w-4 text-blue-600 mt-0.5" />
                          {safetyScore !== null && (
                            <Badge className={`text-xs border ${
                              safetyScore >= 70 ? 'bg-green-100 text-green-800 border-green-300' :
                              safetyScore >= 30 ? 'bg-amber-100 text-amber-800 border-amber-300' :
                              'bg-red-100 text-red-800 border-red-300'
                            }`}>
                              {safetyScore}/100
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium truncate">{doc.filename}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                        </p>
                      </button>
                    );
                  })
                )}
                {documents.length < total && (
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="w-full mt-3 py-2.5 text-sm font-medium text-slate-500 border border-dashed border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
                      </span>
                    ) : (
                      `Charger ${Math.min(LIMIT, total - documents.length)} documents de plus (${total - documents.length} restants)`
                    )}
                  </button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right - Analysis Panel */}
          <div className="lg:col-span-3">
            {loadingDoc ? (
              <Card>
                <CardContent className="py-20 text-center">
                  <Loader2 className="h-10 w-10 animate-spin mx-auto mb-3 text-blue-600" />
                  <p className="text-slate-600">Chargement du document...</p>
                </CardContent>
              </Card>
            ) : !selectedDocument ? (
              <Card>
                <CardContent className="py-20 text-center">
                  <FileText className="h-16 w-16 mx-auto mb-4 opacity-50 text-slate-400" />
                  <p className="text-slate-600">Sélectionnez un document ou uploadez-en un nouveau</p>
                </CardContent>
              </Card>
            ) : !selectedDocument.analysis ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Scale className="h-12 w-12 mx-auto mb-4 text-blue-300" />
                  <p className="text-slate-700 font-medium mb-2">Document prêt pour l'analyse</p>
                  <p className="text-slate-500 text-sm mb-6">L'IA va détecter le type de document et analyser toutes les clauses problématiques</p>
                  <Button onClick={() => handleAnalyze(selectedDocument.id)} disabled={analyzing} className="gap-2 bg-blue-600">
                    {analyzing ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Analyse en cours (peut prendre 30-60s)...</>
                    ) : "Lancer l'analyse IA"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Score Card */}
                <Card className={getRiskBg(100 - selectedDocument.analysis.risk_score)}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h2 className="text-lg font-bold text-slate-900 truncate max-w-xs">{selectedDocument.filename}</h2>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs bg-white">
                            {selectedDocument.document_type || selectedDocument.analysis?.executive_summary?.document_type || "Document juridique"}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {selectedDocument.analysis?.executive_summary?.total_words?.toLocaleString('fr-FR')} mots
                          </span>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-600 mb-1">Score de Sûreté</p>
                        <div className={`text-4xl font-bold ${getRiskColor(100 - selectedDocument.analysis.risk_score)}`}>
                          {100 - selectedDocument.analysis.risk_score}<span className="text-xl">/100</span>
                        </div>
                      </div>
                    </div>
                    <Progress value={100 - selectedDocument.analysis.risk_score} className="h-2" />
                  </CardContent>
                </Card>

                {/* Clause Analysis */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Clauses Problématiques & Suggestions IA</CardTitle>
                      <Badge variant="outline">{selectedDocument.analysis.sensitive_clauses?.length || 0} clause(s)</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(!selectedDocument.analysis.sensitive_clauses || selectedDocument.analysis.sensitive_clauses.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-500" />
                        <p>Aucune clause problématique détectée</p>
                      </div>
                    )}

                    {selectedDocument.analysis.sensitive_clauses?.map((clause, index) => {
                      const sevConf = SEVERITY_CONFIG[clause.risk_level || clause.severity] || SEVERITY_CONFIG.medium;
                      const isExpanded = expandedClauses[index];
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`border-2 rounded-xl overflow-hidden ${
                            selectedSuggestions.includes(index) ? 'border-blue-500' : 'border-slate-200'
                          }`}
                        >
                          {/* Clause Header — always visible */}
                          <div
                            className={`p-4 cursor-pointer flex items-start gap-3 ${
                              selectedSuggestions.includes(index) ? 'bg-blue-50' : 'bg-white hover:bg-slate-50'
                            }`}
                            onClick={() => toggleExpandClause(index)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedSuggestions.includes(index)}
                              onChange={(e) => { e.stopPropagation(); toggleSuggestion(index); }}
                              className="mt-1 h-5 w-5 rounded border-slate-300 text-blue-600 cursor-pointer flex-shrink-0"
                              onClick={e => e.stopPropagation()}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-slate-900 text-sm">{clause.type}</h3>
                                  <Badge className={`text-xs border ${sevConf.bg}`}>
                                    {sevConf.label}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingClauseIndex(index);
                                      setEditedSuggestion(clause.suggested_modification);
                                    }}
                                  >
                                    <Edit3 className="h-3.5 w-3.5" />
                                  </Button>
                                  {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                                </div>
                              </div>
                              {/* Explanation preview */}
                              {clause.explanation && (
                                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{clause.explanation}</p>
                              )}
                              {/* Legal refs */}
                              {clause.legal_references && clause.legal_references.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {clause.legal_references.map((ref, i) => (
                                    <span key={i} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                      <BookOpen className="h-3 w-3" />{ref}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Expanded content */}
                          {isExpanded && (
                            <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50">
                              {/* Texte actuel */}
                              <div>
                                <p className="text-xs font-semibold text-red-700 mb-1 uppercase tracking-wide">Texte actuel :</p>
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-slate-800 leading-relaxed">
                                  {clause.original_text}
                                </div>
                              </div>

                              {/* Modification */}
                              {editingClauseIndex === index ? (
                                <div>
                                  <p className="text-xs font-semibold text-green-700 mb-1 uppercase tracking-wide">Modification (éditable) :</p>
                                  <textarea
                                    value={editedSuggestion}
                                    onChange={(e) => setEditedSuggestion(e.target.value)}
                                    className="w-full p-3 border border-green-300 rounded-lg text-sm min-h-[120px] focus:ring-2 focus:ring-green-500"
                                  />
                                  <div className="flex gap-2 mt-2">
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => {
                                      selectedDocument.analysis.sensitive_clauses[index].suggested_modification = editedSuggestion;
                                      setEditingClauseIndex(null);
                                      toast.success("Modification mise à jour");
                                    }}>
                                      <Check className="h-3 w-3 mr-1" /> Enregistrer
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingClauseIndex(null)}>
                                      <X className="h-3 w-3 mr-1" /> Annuler
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-xs font-semibold text-green-700 mb-1 uppercase tracking-wide">Clause révisée (prête à l'emploi) :</p>
                                  <div className="p-3 bg-green-50 border border-green-300 rounded-lg text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                                    {clause.suggested_modification}
                                  </div>
                                </div>
                              )}

                              {/* Justification */}
                              {clause.modification_reason && (
                                <div>
                                  <p className="text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Justification :</p>
                                  <p className="text-sm text-slate-700 leading-relaxed">{clause.modification_reason}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </motion.div>
                      );
                    })}

                    {/* Action Buttons */}
                    {selectedDocument.analysis.sensitive_clauses?.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-4 border-t">
                        <Button
                          onClick={() => setSelectedSuggestions(selectedDocument.analysis.sensitive_clauses.map((_, i) => i))}
                          variant="outline" size="sm"
                        >
                          Tout sélectionner
                        </Button>
                        <Button
                          onClick={() => setSelectedSuggestions([])}
                          variant="outline" size="sm"
                        >
                          Désélectionner
                        </Button>
                        <Button
                          onClick={() => setShowPreviewModal(true)}
                          variant="outline" size="sm"
                          disabled={selectedSuggestions.length === 0}
                          className="gap-2"
                        >
                          <Eye className="h-4 w-4" /> Prévisualiser le document
                        </Button>
                        <div className="flex-1 min-w-0" />
                        <Badge variant="outline" className="px-3 flex items-center">
                          {selectedSuggestions.length} sélectionnée(s)
                        </Badge>
                        <Button
                          onClick={handleApplyModifications}
                          disabled={selectedSuggestions.length === 0 || applying}
                          className="gap-2 bg-green-600 hover:bg-green-700"
                        >
                          {applying ? (
                            <><Loader2 className="h-4 w-4 animate-spin" /> Application...</>
                          ) : (
                            <><Download className="h-4 w-4" /> Télécharger le document modifié</>
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal — Full document */}
      {showPreviewModal && selectedDocument && (() => {
        const preview = buildPreviewContent();
        if (!preview) return null;
        const segments = preview.text.split(/(\[\[MOD_START\]\].*?\[\[MOD_END\]\])/s);
        return (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreviewModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-5 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Eye className="h-5 w-5 text-blue-600" />
                  <div>
                    <h2 className="text-lg font-semibold">Document complet avec modifications</h2>
                    <p className="text-xs text-slate-500">{selectedDocument.filename}</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800 border-green-300">
                    {selectedSuggestions.length} modification(s) appliquée(s)
                  </Badge>
                </div>
                <button onClick={() => setShowPreviewModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8" style={{ fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: '1.8' }}>
                  <h1 className="text-xl font-bold text-center text-blue-900 mb-6 pb-4 border-b">
                    {selectedDocument.filename.replace(/\.[^/.]+$/, '')}
                  </h1>
                  <div className="whitespace-pre-wrap text-slate-800">
                    {segments.map((seg, i) => {
                      if (seg.startsWith('[[MOD_START]]')) {
                        const content = seg.replace('[[MOD_START]]', '').replace('[[MOD_END]]', '');
                        return (
                          <span key={i} className="bg-green-100 border-l-4 border-green-500 pl-2 text-green-900 rounded">
                            {content}
                          </span>
                        );
                      }
                      return <span key={i}>{seg}</span>;
                    })}
                  </div>
                </div>
              </div>

              <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
                <p className="text-xs text-slate-500">
                  Les sections surlignées en vert correspondent aux clauses modifiées
                </p>
                <Button onClick={handleApplyModifications} disabled={applying} className="gap-2 bg-green-600 hover:bg-green-700">
                  <Download className="h-4 w-4" />
                  {applying ? "Téléchargement..." : "Télécharger en PDF"}
                </Button>
              </div>
            </motion.div>
          </div>
        );
      })()}
    </Layout>
  );
}
